import React, { useState } from 'react';
import { Pencil, Archive, X, Save } from 'lucide-react';

/**
 * Floating admin controls shown on every social ad card:
 *   - "Edit Copy" — opens a small side panel with editable text fields
 *   - "Archive"   — hides this ad from the /social-ads list
 *
 * Props:
 *   templateKey: string   — unique key used to store overrides + archive state
 *   fields: [{ key, label, type, placeholder, rows? }]
 *   values: current values object
 *   onSave(patch): async — persist patch to backend
 *   onArchive():   async — archive this template
 */
const AdControls = ({ templateKey, fields = [], values = {}, onSave, onArchive }) => {
  const [openEdit, setOpenEdit] = useState(false);
  const [draft, setDraft] = useState(values);
  const [saving, setSaving] = useState(false);

  const startEdit = () => {
    setDraft({ ...values });
    setOpenEdit(true);
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave(draft);
    setSaving(false);
    setOpenEdit(false);
  };

  const handleArchive = async () => {
    if (!window.confirm('Archive this template? It will disappear from the Ad Templates list. You can restore it later from the Admin.')) return;
    await onArchive();
  };

  return (
    <>
      <div
        className="absolute top-3 right-3 z-[60] flex gap-2"
        style={{ pointerEvents: 'auto' }}
        data-testid={`ad-controls-${templateKey}`}
      >
        {fields.length > 0 && (
          <button
            onClick={startEdit}
            title="Edit ad copy"
            data-testid={`edit-copy-${templateKey}`}
            className="flex items-center gap-1.5 bg-white/95 hover:bg-white text-[#1a4d2e] text-xs font-bold px-3 py-1.5 rounded-full shadow-lg backdrop-blur border border-white/80 transition-all"
          >
            <Pencil className="w-3.5 h-3.5" /> Edit Copy
          </button>
        )}
        <button
          onClick={handleArchive}
          title="Archive template"
          data-testid={`archive-${templateKey}`}
          className="flex items-center gap-1.5 bg-white/95 hover:bg-red-50 hover:text-red-700 text-gray-800 text-xs font-bold px-3 py-1.5 rounded-full shadow-lg backdrop-blur border border-white/80 transition-all"
        >
          <Archive className="w-3.5 h-3.5" /> Archive
        </button>
      </div>

      {openEdit && (
        <div
          className="fixed inset-0 bg-black/60 z-[80] flex items-center justify-center p-4"
          data-testid={`edit-copy-modal-${templateKey}`}
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-5 border-b border-gray-200 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-bold text-[#1a4d2e]">Edit Ad Copy</h3>
                <p className="text-xs text-gray-500">Changes save to this template and update the download instantly.</p>
              </div>
              <button
                onClick={() => setOpenEdit(false)}
                className="text-gray-400 hover:text-gray-700 p-1"
                data-testid={`edit-copy-close-${templateKey}`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 overflow-y-auto space-y-4 flex-1">
              {fields.map(f => (
                <div key={f.key}>
                  <label className="block text-xs font-bold text-[#1a4d2e] tracking-wide uppercase mb-1.5">
                    {f.label}
                  </label>
                  {f.type === 'textarea' ? (
                    <textarea
                      value={draft[f.key] ?? ''}
                      onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                      rows={f.rows || 3}
                      placeholder={f.placeholder}
                      data-testid={`edit-copy-field-${templateKey}-${f.key}`}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#ff6b00]"
                    />
                  ) : (
                    <input
                      type="text"
                      value={draft[f.key] ?? ''}
                      onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                      placeholder={f.placeholder}
                      data-testid={`edit-copy-field-${templateKey}-${f.key}`}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#ff6b00]"
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => setOpenEdit(false)}
                className="px-4 py-2 text-sm rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200"
              >Cancel</button>
              <button
                onClick={handleSave}
                disabled={saving}
                data-testid={`edit-copy-save-${templateKey}`}
                className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-[#ff6b00] text-white hover:bg-[#ff6b00]/90 disabled:opacity-60 font-semibold"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdControls;
